/**
 * LegislatorsReport
 * 
 * Standard legislators report. Uses shared legislator components.
 * Features: My/All toggle, table/card views, export, public view support.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';
import { 
  Users, 
  List, 
  LayoutGrid
} from 'lucide-react';
import { useWorkspaceReports } from '../../hooks/useWorkspaceReports';
import StatefulIcon from '../StatefulIcon';
import ReportLogoHeader from './ReportLogoHeader';

// Shared legislator components
import ExportMenu from '../ExportMenu';
import {
  BaseLegislator,
  LegislatorRenderContext,
  ViewMode,
  useLegislatorData,
  LegislatorTable,
  LegislatorCardGrid,
  LegislatorTableColumn,
  SortField,
  SortDirection,
  CURRENT_SESSION,
  normalizeLegislator,
} from '../legislators';

// =============================================================================
// Types
// =============================================================================

interface LegislatorsReportProps {
  workspaceId?: string;
  workspaceName?: string;
  organizationId?: string;
  organizationName?: string;
  isPublicView?: boolean;
  /** Pre-fetched legislators data (used by public report page to avoid re-fetching) */
  prefetchedLegislators?: ReportLegislator[];
  /** Called when the internal card/table toggle changes — used by PublicReportPage to sync export format */
  onViewModeChange?: (mode: ViewMode) => void;
}

interface ReportLegislator extends BaseLegislator {
  sponsored_bills_count?: number;
  sponsoredBillsCount?: number;
}

type LegislatorSource = 'my' | 'all';

// =============================================================================
// Component
// =============================================================================

const LegislatorsReport: React.FC<LegislatorsReportProps> = ({ 
  workspaceId,
  // workspaceName kept for interface compatibility but not displayed in title
  workspaceName: _workspaceName = 'Workspace',
  organizationId,
  organizationName: _organizationName,
  isPublicView = false,
  prefetchedLegislators,
  onViewModeChange,
}) => {
  const isOrgMode = !!organizationId;
  const hasPrefetched = Array.isArray(prefetchedLegislators);
  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('card');

  // Notify parent when view mode changes (e.g. PublicReportPage needs to know for export format)
  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    onViewModeChange?.(mode);
  }, [onViewModeChange]);
  const [legislatorSource, setLegislatorSource] = useState<LegislatorSource>('my');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const { exportReport } = useWorkspaceReports(workspaceId, organizationId);

  // Org-mode fetch — skip if pre-fetched data was supplied (e.g. public report page)
  const { data: orgLegislators = [], isLoading: orgLoading } = useQuery(
    ['org-legislators', organizationId],
    async () => {
      const response = await api.get(`/org-reports/${organizationId}/legislators`);
      return response.data.data || [];
    },
    { enabled: isOrgMode && !hasPrefetched, staleTime: 30000 }
  );

  // Data fetching based on source (workspace mode)
  const { data: wsReportData, isLoading: wsLoading, error } = useLegislatorData<ReportLegislator>({
    source: legislatorSource === 'my' ? 'report' : 'report-all',
    workspaceId,
    isPublicView,
    enabled: !isOrgMode,
  });

  const reportData = useMemo(
    () => isOrgMode
      ? (hasPrefetched ? (prefetchedLegislators as ReportLegislator[]) : (orgLegislators as ReportLegislator[]))
      : (wsReportData || []),
    [isOrgMode, hasPrefetched, prefetchedLegislators, orgLegislators, wsReportData]
  );
  const isLoading = isOrgMode ? (hasPrefetched ? false : orgLoading) : wsLoading;

  // In org mode: aggregate workspace info per legislator and normalize to camelCase.
  // The backend already deduplicates and attaches workspaces[] when using orgReportBuilderService.
  // If workspaces[] is present on the first record, trust it directly.
  // Otherwise fall back to manually deduplicating flat per-workspace rows (legacy shape).
  const deduplicatedLegislators = useMemo(() => {
    if (!isOrgMode || !reportData || reportData.length === 0) return null;

    // Backend pre-aggregated path: workspaces[] already built server-side
    if (Array.isArray((reportData[0] as any)?.workspaces)) {
      return reportData.map(leg => ({
        ...normalizeLegislator(leg),
        workspaces: (leg as any).workspaces,
      }));
    }

    // Legacy flat path: one row per legislator×workspace, deduplicate client-side
    const map = new Map<string | number, ReportLegislator & { workspaces: Array<{ name: string; id: string; position: string }> }>();
    for (const leg of reportData) {
      const normalized = normalizeLegislator(leg);
      const key = (leg as any).legislator_id ?? leg.id;
      const wsEntry = {
        name: (leg as any).workspace_name || '',
        id: (leg as any).workspace_id || String(key),
        position: (leg as any).position || '',
      };
      if (map.has(key)) {
        map.get(key)!.workspaces.push(wsEntry);
      } else {
        map.set(key, { ...normalized, workspaces: [wsEntry] });
      }
    }
    return Array.from(map.values());
  }, [isOrgMode, reportData]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!reportData) return [];
    
    // In org mode use deduplicated list for sorting
    const dataToSort = isOrgMode && deduplicatedLegislators ? deduplicatedLegislators : reportData;

    return [...dataToSort].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'name': {
          const aName = a.last_name || a.lastName || '';
          const bName = b.last_name || b.lastName || '';
          comparison = aName.localeCompare(bName);
          break;
        }
        case 'chamber':
          comparison = (a.chamber || '').localeCompare(b.chamber || '');
          break;
        case 'district':
          comparison = parseInt(a.district || '0') - parseInt(b.district || '0');
          break;
        case 'party': {
          const aParty = a.party_affiliation || a.partyAffiliation || '';
          const bParty = b.party_affiliation || b.partyAffiliation || '';
          comparison = aParty.localeCompare(bParty);
          break;
        }
        default:
          comparison = 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [reportData, deduplicatedLegislators, isOrgMode, sortField, sortDirection]);

  // Handle sort
  const handleSort = (field: string) => {
    const sortKey = field as SortField;
    if (sortKey === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(sortKey);
      setSortDirection('asc');
    }
  };

  // Handle click outside to close export menu
  const handleExport = async (format: string) => {
    try {
      // Pass viewMode so backend uses landscape for table and portrait for card PDF
      await exportReport('legislators', format as 'csv' | 'excel' | 'pdf' | 'word', {
        viewMode,
        includeFullDetails: viewMode === 'card',
      } as any);
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  // ==========================================================================
  // Render Context
  // ==========================================================================

  const renderContext: LegislatorRenderContext = useMemo(() => ({
    workspaceId,
    isPublicView,
    // Use public links only in public view; authenticated reports use authenticated legislator routes
    usePublicLinks: isPublicView,
  }), [workspaceId, isPublicView]);

  // Table columns for workspace report view
  const tableColumns: LegislatorTableColumn[] = [
    'name',
    'chamber',
    'district',
    'party',
    'committees',
    'contact',
    'sponsored_bills',
    'votes',
  ];

  // Table columns for org report view — workspaces second (right after name) per org-report convention.
  const orgTableColumns: LegislatorTableColumn[] = [
    'name',
    'workspaces',
    'chamber',
    'district',
    'party',
    'committees',
    'contact',
    'sponsored_bills',
    'votes',
  ];

  // ==========================================================================
  // Render
  // ==========================================================================

  // Loading state
  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-text-tertiary dark:text-text-dark-tertiary">Loading legislators...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-error-500 dark:text-error-400">
            Error loading legislators: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Report Logo Header */}
      <ReportLogoHeader workspaceId={workspaceId} />
      
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-left text-text-primary dark:text-text-dark-primary flex items-center gap-2">
          <Users className="h-5 w-5 text-success-600" />
          Legislators Report {CURRENT_SESSION}
        </h3>
        <p className="text-sm text-left text-text-secondary dark:text-text-dark-secondary mt-1">
          {legislatorSource === 'my' ? 'Tracked legislators' : 'All active legislators'} with contact information and committees
        </p>
      </div>

      {/* Controls Row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        {/* Left side - My/All Toggle and Summary */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          {/* My/All Legislators Toggle — only in workspace (non-org) mode */}
          {!isOrgMode && (
          <div className="flex items-center bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg p-1">
            <button
              onClick={() => setLegislatorSource('my')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                legislatorSource === 'my'
                  ? 'bg-white dark:bg-background-dark-secondary text-primary-700 dark:text-primary-300 shadow-sm'
                  : 'text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary'
              }`}
            >
              My Legislators
            </button>
            <button
              onClick={() => setLegislatorSource('all')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                legislatorSource === 'all'
                  ? 'bg-white dark:bg-background-dark-secondary text-primary-700 dark:text-primary-300 shadow-sm'
                  : 'text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary'
              }`}
            >
              All Legislators
            </button>
          </div>
          )}

          {/* Summary */}
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-text-tertiary" />
            <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
              {sortedData.length} {isOrgMode ? 'tracked' : legislatorSource === 'my' ? 'tracked' : 'total'} legislators
            </span>
          </div>
        </div>

        {/* Right side - View Toggle and Export */}
        <div className="flex items-center gap-2">
          {/* View Toggle - Hidden on mobile */}
          <div className="hidden md:flex items-center space-x-2">
            <button
              onClick={() => handleViewModeChange('card')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'card'
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                  : 'bg-background-tertiary text-text-secondary hover:bg-neutral-200 dark:bg-background-dark-tertiary dark:text-text-dark-tertiary'
              }`}
              title="Card View"
            >
              <StatefulIcon icon={LayoutGrid} active={viewMode === 'card'} size="md" />
            </button>
            <button
              onClick={() => handleViewModeChange('table')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'table'
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                  : 'bg-background-tertiary text-text-secondary hover:bg-neutral-200 dark:bg-background-dark-tertiary dark:text-text-dark-tertiary'
              }`}
              title="Table View"
            >
              <StatefulIcon icon={List} active={viewMode === 'table'} size="md" />
            </button>
          </div>

          {/* Export Menu — hidden in public view (parent page handles export) */}
          <ExportMenu
            formats={viewMode === 'table' ? ['csv', 'excel', 'pdf'] : ['word', 'pdf']}
            onExport={(format) => handleExport(format)}
          />
        </div>
      </div>

      {/* Report Content */}
      {sortedData.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-text-tertiary dark:text-text-dark-tertiary mx-auto mb-4" />
          <h4 className="text-lg font-medium text-text-primary dark:text-text-dark-primary mb-2">
            {isOrgMode ? 'No Tracked Legislators' : legislatorSource === 'my' ? 'No Tracked Legislators' : 'No Legislators Found'}
          </h4>
          <p className="text-text-secondary dark:text-text-dark-secondary">
            {isOrgMode
              ? 'No legislators are being tracked in any workspace in this organization.'
              : legislatorSource === 'my'
                ? 'Start tracking legislators to see them in this report.'
                : 'No active legislators found matching your filters.'}
          </p>
        </div>
      ) : isOrgMode ? (
        /* Org mode: same card/table toggle as workspace mode, workspaces column in table */
        <>
          {/* Mobile: Card view only */}
          <div className="md:hidden">
            <LegislatorCardGrid
              data={sortedData}
              context={renderContext}
              config={{
                showContact: true,
                showCommittees: true,
                showSponsoredBills: true,
                showVotingRecords: true,
              }}
              gridCols="grid-cols-1"
              linkDistrictMap={false}
            />
          </div>

          {/* Desktop: User choice */}
          <div className="hidden md:block">
            {viewMode === 'table' ? (
              <LegislatorTable
                data={sortedData}
                isLoading={isLoading}
                columns={orgTableColumns}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                context={renderContext}
                isReportView={true}
              />
            ) : (
              <LegislatorCardGrid
                data={sortedData}
                context={renderContext}
                config={{
                  showContact: true,
                  showCommittees: true,
                  showSponsoredBills: true,
                  showVotingRecords: true,
                }}
                gridCols="grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                linkDistrictMap={false}
              />
            )}
          </div>
        </>
      ) : (
        <>
          {/* Mobile: Card view only */}
          <div className="md:hidden">
            <LegislatorCardGrid
              data={sortedData}
              context={renderContext}
              config={{
                showContact: true,
                showCommittees: true,
                showSponsoredBills: true,
                showVotingRecords: true,
              }}
              gridCols="grid-cols-1"
              linkDistrictMap={false}
            />
          </div>

          {/* Desktop: User choice */}
          <div className="hidden md:block">
            {viewMode === 'table' ? (
              <LegislatorTable
                data={sortedData}
                isLoading={isLoading}
                columns={tableColumns}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                context={renderContext}
                isReportView={true}
              />
            ) : (
              <LegislatorCardGrid
                data={sortedData}
                context={renderContext}
                config={{
                  showContact: true,
                  showCommittees: true,
                  showSponsoredBills: true,
                  showVotingRecords: true,
                }}
                gridCols="grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                linkDistrictMap={false}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default LegislatorsReport;
